import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Database, FileSpreadsheet, Download, Loader2, CheckSquare, Square } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Select } from "@/components/Kit";

const LABELS = {
  schools: "Schools", classes: "Classes & Sections", students: "Students (with fee total / paid / balance)",
  fee_structure: "Fee Structure", fee_receipts: "Fee Receipts", fee_receipt_items: "Fee Receipt Line Items",
  attendance: "Attendance (one row per student per day)", exams: "Exams", exam_marks: "Exam Marks",
};

function downloadBlob(res) {
  const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "export";
  const url = URL.createObjectURL(res.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
  return name;
}

export default function DataExport() {
  const [schoolId, setSchoolId] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState("");

  const { data: schools } = useQuery({ queryKey: ["sa-schools"], queryFn: async () => (await api.get("/superadmin/schools")).data });
  const { data: preview, isLoading } = useQuery({
    queryKey: ["sa-export-preview", schoolId],
    queryFn: async () => (await api.get("/superadmin/export/preview", { params: schoolId ? { school_id: schoolId } : {} })).data,
  });
  const tables = preview?.tables || [];
  const selected = picked ?? tables;
  const toggle = (t) => setPicked(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  const totalRows = selected.reduce((a, t) => a + (preview?.counts?.[t] || 0), 0);

  const download = async (format) => {
    if (!selected.length) return toast.error("Select at least one table");
    setBusy(format);
    try {
      const params = { format, tables: selected.join(",") };
      if (schoolId) params.school_id = schoolId;
      const res = await api.get("/superadmin/export/download", { params, responseType: "blob" });
      toast.success(`Downloaded ${downloadBlob(res)}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="export-page">
      <PageHeader title="Data Export" subtitle="One-click SQL / CSV dumps ready for Azure SQL Database" />

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="ep-card p-5">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <Select label="Scope" value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setPicked(null); }} data-testid="export-school-select">
              <option value="">All schools</option>
              {(schools || []).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </Select>
            <button type="button" className="text-xs text-indigo-300 hover:text-white" onClick={() => setPicked(selected.length === tables.length ? [] : tables)} data-testid="export-toggle-all">
              {selected.length === tables.length ? "Clear all" : "Select all"}
            </button>
          </div>

          <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
            {isLoading && <div className="p-6 text-sm text-slate-400" data-testid="export-loading">Counting rows…</div>}
            {tables.map((t) => {
              const on = selected.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggle(t)} data-testid={`export-table-${t}`}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-800/40 ${on ? "" : "opacity-50"}`}>
                  {on ? <CheckSquare className="h-4 w-4 text-indigo-400" /> : <Square className="h-4 w-4 text-slate-500" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{LABELS[t]}</p>
                    <p className="font-mono text-[11px] text-slate-500">dbo.{t}</p>
                  </div>
                  <span className="font-mono text-sm text-slate-300" data-testid={`export-count-${t}`}>{preview?.counts?.[t] ?? "–"} rows</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="ep-card p-5">
            <p className="text-xs uppercase tracking-wider text-slate-400">Selected</p>
            <p className="mt-1 text-3xl font-bold font-head text-white" data-testid="export-total-rows">{totalRows.toLocaleString("en-IN")}</p>
            <p className="text-xs text-slate-500">rows across {selected.length} table{selected.length === 1 ? "" : "s"}</p>
          </div>

          <button onClick={() => download("sql")} disabled={!!busy} data-testid="export-download-sql"
            className="ep-btn flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold">
            {busy === "sql" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Download .sql (T-SQL)
          </button>
          <button onClick={() => download("csv")} disabled={!!busy} data-testid="export-download-csv"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
            {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Download CSV bundle (.zip)
          </button>

          <div className="ep-card p-4 text-xs leading-relaxed text-slate-400">
            <p className="mb-1 flex items-center gap-1.5 font-semibold text-slate-200"><Download className="h-3.5 w-3.5" /> Load into Azure SQL</p>
            <p><span className="text-slate-200">.sql</span> — open in SSMS / Azure Data Studio and run. Creates tables (drops existing) and inserts every row.</p>
            <p className="mt-1"><span className="text-slate-200">.zip</span> — one CSV per table; use SSMS “Import Flat File”, BULK INSERT or Azure Data Factory.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
